import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { RootState } from "../../Controllers/Store";
import { useSelector } from "react-redux";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";
import './Css/BuildingControl.css';
import './Css/BuildingControl-responsive.css';

import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import PageLoader from "../../Components/PageLoader";
import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import { goToTop } from "../../Components/goToTop";

import { BuildingControlProjectsType } from "../../Controllers/Types/BuildingControl/BuildingControlProjectsType";
import { fetchAllBuildingControlProjects } from "../../Controllers/Thunks/BuildingControlThunks";
import { addProjectForBuildingControl } from "../../Api/BuildingControlService/BuildingControlService";
import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import { fetchAllProjects } from "../../Controllers/Thunks/projectThunks";
import { OverlayPanel } from "primereact/overlaypanel";

const BuildingControlProjects: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const toast = useRef<Toast>(null);
    const projectInfoPanel = useRef<OverlayPanel>(null);

    const loadingMasterProjectData: boolean = useAppSelector((state) => state.project.loading);
    const masterProjectData: Project[] = useAppSelector((state) => state.project.allProjects);
    const loadingBuildingControlProjects: boolean = useAppSelector((state) => state.buildingControl.loadingBuildingControlProjects);
    const errorBuildingControlProjects: string | null = useAppSelector((state) => state.buildingControl.errorBuildingControlProjects);
    const buildingControlProjects: BuildingControlProjectsType[] = useAppSelector((state) => state.buildingControl.buildingControlProjects);

    const [loading, setLoading] = useState<boolean>(false);
    const tabs = ["All", "Current", "Ongoing", "Completed"] as const;
    type ProjectFilter = typeof tabs[number];
    const [projectsFilterState, setProjectsFilterState] = useState<ProjectFilter>("All");
    const [showAddProjectsModal, setShowAddProjectsModal] = useState<boolean>(false);
    const [selectedProjects, setSelectedProjects] = useState<BuildingControlProjectsType[]>([]);
    const [loadingViewProjectId, setLoadingViewProjectId] = useState<number | null>(null);
    const [projectsData, setProjectsData] = useState<BuildingControlProjectsType[]>([]);
    const [buildingControlProjectsData, setBuildingControlProjectsData] = useState<BuildingControlProjectsType[]>([]);
    const [filteredBuildingControlProjects, setFilteredBuildingControlProjects] = useState<BuildingControlProjectsType[]>([]);
    const [projectInactiveMessage, setProjectInactiveMessage] = useState<string>('');

    // Permision check
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const is_permision_add_project = useCanEdit("Building Control", "add_project");

    // fetch data initially
    useEffect(() => {
        dispatch(fetchAllBuildingControlProjects());
        dispatch(fetchAllProjects());
    }, [dispatch]);

    // Initial data set
    useEffect(() => {
        setProjectsData(buildingControlProjects.filter((project) => project?.is_building_control_project_list === false));
        setBuildingControlProjectsData(buildingControlProjects.filter((project) => project?.is_building_control_project_list === true));
    }, [buildingControlProjects]);

    const getProjectsCount = (type: ProjectFilter): number => {
        if (!buildingControlProjectsData) return 0;

        if (type === "All") {
            return buildingControlProjectsData.length;
        }

        return buildingControlProjectsData.filter(
            (project) => project?.status?.toLowerCase() === type?.toLowerCase()
        )?.length;
    };

    useEffect(() => {
        const choosedActiveTab = localStorage.getItem("activeBuildingControlProjectTab") as ProjectFilter;
        if (choosedActiveTab) {
            setProjectsFilterState(choosedActiveTab);
        } else {
            setProjectsFilterState('All');
        }
    }, []);

    useEffect(() => {
        const fetchFilteredData = () => {
            if (projectsFilterState === "All") {
                const allProjects = buildingControlProjectsData;
                setFilteredBuildingControlProjects(allProjects);
            } else if (projectsFilterState === "Current") {
                const currentProjects = buildingControlProjectsData?.filter(
                    (project) => project?.status === "Current"
                );
                setFilteredBuildingControlProjects(currentProjects);
            } else if (projectsFilterState === "Ongoing") {
                const ongoingProjects = buildingControlProjectsData?.filter(
                    (project) => project?.status === "Ongoing"
                );
                setFilteredBuildingControlProjects(ongoingProjects);
            } else if (projectsFilterState === "Completed") {
                const completedProjects = buildingControlProjectsData?.filter(
                    (project) => project?.status === "Completed"
                );
                setFilteredBuildingControlProjects(completedProjects);
            }
        };

        fetchFilteredData();
    }, [projectsFilterState, buildingControlProjectsData]);

    const handleFilterProjects = (filterState: ProjectFilter) => {
        localStorage.setItem("activeBuildingControlProjectTab", filterState);
        setProjectsFilterState(filterState);
        goToTop();
    };

    // for table actions
    const tableActionBody = (rowData: BuildingControlProjectsType) => {
        const project: BuildingControlProjectsType = rowData;

        return (
            <div className="tabel_btn_area mt-1 mb-1">
                {handleCheckIsProjectActive(project) === true ? (
                    <Button
                        key={project?.project_id}
                        icon={`bi bi-eye`}
                        label={"View"}
                        className="tabel_btn view_btn fixed_width"
                        onClick={() => handleViewProject(project)}
                        loading={loadingViewProjectId === project?.project_id}
                    />
                ) : (
                    <Button
                        key={project?.project_id}
                        icon={`ri ri-lock-fill`}
                        label={"Locked"}
                        className="tabel_btn view_btn fixed_width"
                        onClick={(e) => handleViewProjectInactiveInfo(e, 'The project license has expired. Please go to Projects and renew the license.')}
                    />
                )}
            </div>
        );
    };

    const handleViewProjectInactiveInfo = (e: React.MouseEvent<HTMLButtonElement>, message: string) => {
        if (projectInfoPanel.current) {
            projectInfoPanel.current.hide();
            setTimeout(() => projectInfoPanel.current?.show(e, e.currentTarget), 50);
        }

        setProjectInactiveMessage(message);
    };

    const handleViewProject = (project: BuildingControlProjectsType) => {
        setLoadingViewProjectId(project?.project_id);

        const mainProject = masterProjectData?.find(masterProject => masterProject?.project_id === project?.project_id);

        if (mainProject && mainProject !== null) {
            setLoadingViewProjectId(null);

            navigate(`/project/detail`, { state: { projectData: mainProject } });
            localStorage.setItem("activeCategoryTab", 'Building Control');
            goToTop();
        } else {
            setLoadingViewProjectId(null);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error viewing project details.",
                    life: 3000,
                });
            }
        }

    }

    const getRowClassNameForProject = (data: BuildingControlProjectsType, options: { [key: string]: any }) => {
        const projectType = data.project_type;
        const status = data.status;
        const isProjectInactive: boolean = (!handleCheckIsProjectActive(data));

        if (status === 'Current') {
            if (projectType === "CP") {
                if (isProjectInactive) {
                    return "project_data_row current locked commercial ";
                } else {
                    return "project_data_row current commercial";
                }
            } else if (projectType === "DP") {
                if (isProjectInactive) {
                    return "project_data_row current locked domestic";
                } else {
                    return "project_data_row current domestic";
                }
            }
            return "project_data_row current";
        } else if (status === 'Ongoing') {
            if (isProjectInactive) {
                return "project_data_row ongoing locked";
            } else {
                return "project_data_row ongoing";
            }
        } else if (status === 'Completed') {
            if (isProjectInactive) {
                return "project_data_row completed locked";
            } else {
                return "project_data_row completed";
            }
        }
    };

    /* ******************************* Add project ******************************* */
    const handleCloseProjectModal = () => {
        setShowAddProjectsModal(false);
        setSelectedProjects([]);
    };

    const handleAddProject = () => {
        setShowAddProjectsModal(true);
    };

    function checkCredentials(): boolean {
        return selectedProjects.length > 0;
    }

    const addProjects = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);

            const data = await addProjectForBuildingControl(selectedProjects);

            if (data.statusCode === 200) {
                dispatch(fetchAllBuildingControlProjects());

                setLoading(false);
                handleCloseProjectModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Project(s) added successfully.",
                        life: 3000,
                    });
                }
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error adding the project.",
                        life: 3000,
                    });
                }
            }
        }
    }

    const projectModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Add project(s)
            </h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseProjectModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const projectModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseProjectModal}
            />

            <Button
                disabled={!checkCredentials()}
                label={`${loading ? "Processing" : "Add"}`}
                onClick={addProjects}
                loading={loading}
                className="custom_btn primary"
            />
        </div>
    );

    const handleSelectProjects = (project: BuildingControlProjectsType, isChecked: boolean) => {
        if (isChecked) {
            setSelectedProjects((prevState) => [...prevState, project]);
        } else {
            setSelectedProjects((prevState) =>
                prevState.filter(
                    (existingProject) => existingProject.project_id !== project.project_id
                )
            );
        }
    };

    const isSelectedProject = (project: BuildingControlProjectsType): boolean =>
        selectedProjects.some(
            (existingproject) => existingproject?.project_id === project?.project_id
        );

    const handleCheckIsProjectActive = (project: BuildingControlProjectsType): boolean => {
        let isProjectActive: boolean = false;

        const mainProject: Project | null = masterProjectData?.find(masterProject => masterProject?.project_id === project?.project_id) || null;

        if (mainProject && mainProject !== null) {
            if (mainProject?.this_project_active === 1) {
                isProjectActive = true;
            } else {
                isProjectActive = false;
            }
        } else {
            isProjectActive = false;
        }

        return isProjectActive;
    }
    /* *************************************************************************** */


    /* ***************************** Common function ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <OverlayPanel ref={projectInfoPanel} className="project_info_panel" dismissable>
                <p>{projectInactiveMessage}</p>
                <Link
                    to={`/project`}
                    onClick={() => {
                        goToTop();
                        localStorage.setItem('activeProjectTab', 'all');
                        localStorage.setItem("activeProjectViewType", 'My project');
                        localStorage.setItem("activeSharedFolderCategory", 'Others');
                    }}
                >
                    Projects&nbsp;<i className="bi bi-box-arrow-up-right"></i>
                </Link>
            </OverlayPanel>

            <div className="main_content layout_padding show_side_bar">
                {loadingBuildingControlProjects || loadingMasterProjectData ? (
                    <PageLoader />
                ) : errorBuildingControlProjects ? (
                    <ErrorPage errorMessage={errorBuildingControlProjects} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Building Control <span className="text_empty">- Projects</span></h4>
                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {tabs.map((tab) => (
                                    <div key={tab} className="contact_tab_item">
                                        <button
                                            type="button"
                                            className={`contact_tab_btn ${projectsFilterState === tab ? "active" : ""} p-ripple`}
                                            onClick={() => handleFilterProjects(tab)}
                                        >
                                            {tab}:&nbsp;{String(getProjectsCount(tab)).padStart(2, '0')}
                                            <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {is_permision_add_project && (
                                <div className="page_action_area">
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddProject}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add project</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section p-0">
                            {filteredBuildingControlProjects && filteredBuildingControlProjects?.length > 0 ? (
                                <DataTable
                                    value={filteredBuildingControlProjects}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No project(s) found!"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: "50rem" }}
                                    paginatorDropdownAppendTo={"self"}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    rowClassName={getRowClassNameForProject}
                                    dataKey="project_id"
                                >
                                    <Column
                                        header="Job no."
                                        headerClassName="text_purple"
                                        field="job_number"
                                        body={(rowData: BuildingControlProjectsType) => (
                                            <span className="text-capitalize text_bold">
                                                {rowData?.job_number ? rowData?.job_number : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Type"
                                        headerClassName="text_purple"
                                        field="project_type"
                                        body={(rowData: BuildingControlProjectsType) => (
                                            <span className="text-capitalize">
                                                {rowData?.project_type === "CP" ? "Commercial" : "Domestic"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Address"
                                        headerClassName="text_purple"
                                        field="address"
                                        body={(rowData: BuildingControlProjectsType) => (
                                            <span className="text-capitalize">
                                                {rowData?.address ? rowData?.address : "---"}
                                            </span>
                                        )}
                                        style={{ width: "25%" }}
                                    ></Column>

                                    <Column
                                        header="Description"
                                        headerClassName="text_purple"
                                        field="description"
                                        className="first_letter_capitalized"
                                        body={(rowData: BuildingControlProjectsType) => (
                                            <span>
                                                {rowData?.description ? `${rowData.description.slice(0, 60)}${rowData.description?.length > 60 ? '...' : ''}` : "---"}
                                            </span>
                                        )}
                                        style={{ width: "30%" }}
                                    ></Column>

                                    <Column
                                        body={tableActionBody}
                                        alignHeader="center"
                                        className="table_btn_area"
                                        style={{ width: "15%" }}
                                        alignFrozen="right"
                                        frozen
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`
                                        ${buildingControlProjectsData?.length === 0 ? "No building control project(s) have been added. To add, click the 'Add Project' button."
                                            : filteredBuildingControlProjects?.length === 0 ? `No building control ${projectsFilterState !== 'All' ? (projectsFilterState)?.toLowerCase() : ''} project(s) were found.` :
                                                'No project(s) found!'}
                                                `}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Project selection modal */}
            <Dialog
                visible={showAddProjectsModal}
                headerClassName="custom_modal_header"
                header={projectModalHeader}
                footer={projectModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseProjectModal}
            >
                <div className="custom_modal_body">
                    <div className="p-1">
                        {projectsData && projectsData?.length > 0 ? (
                            <div className="row">
                                <div className="col-12">
                                    <label className={`custom_form_label mb-2`}>
                                        Select project(s)
                                    </label>
                                    {projectsData?.map((project) => (
                                        <div
                                            className="option_checkbox_area"
                                            key={project?.project_id}
                                        >
                                            <Checkbox
                                                onChange={(e) => handleSelectProjects(project, e.checked || false)}
                                                checked={isSelectedProject(project)}
                                                disabled={handleCheckIsProjectActive(project) === false}
                                            />

                                            <Tooltip
                                                id={`project_type${project?.project_id}`}
                                                key={`project_type${project?.project_id}`}
                                                target={`.project_type${project?.project_id}`}
                                            />

                                            <article
                                                className={`shared_contact_body selectable mb-0 ${isSelectedProject(project) && "selected"}
                                                ${handleCheckIsProjectActive(project) === true ? '' : 'disabled'} p-ripple`}
                                                onClick={() => {
                                                    if (handleCheckIsProjectActive(project)) {
                                                        handleSelectProjects(project, !isSelectedProject(project));
                                                    }
                                                }}
                                            >
                                                <h6 className="text-capitalize">
                                                    {project?.job_number}: {project?.address}
                                                </h6>

                                                <h6
                                                    className={`project_type${project?.project_id}`}
                                                    data-pr-tooltip={project?.project_type === "CP" ? "Commercial project" : "Domestic project"}
                                                    data-pr-position="top"
                                                >
                                                    {project?.project_type}
                                                </h6>

                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                            </article>

                                            {!(handleCheckIsProjectActive(project)) && (
                                                <button className="lock_btn is_btn"
                                                    onClick={(e) => handleViewProjectInactiveInfo(e, 'The project license has expired. You cannot add this project. Please go to Projects and renew the license.')}>
                                                    <i className={`ri ri-lock-fill lock_${project?.project_id}`}
                                                    ></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="no_records_found">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                Project(s) not available.
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default BuildingControlProjects;